import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaArrowDown, FaFileAlt } from 'react-icons/fa';
import gsap from 'gsap';
import { siteConfig } from '../data/site';

const Hero = () => {
  const heroRef = useRef(null);
  const whatsappMessage = encodeURIComponent(
    'Olá! Gostaria de solicitar um orçamento para desenvolvimento de site institucional.'
  );
  const whatsappHref = `https://wa.me/${siteConfig.whatsappNumber}?text=${whatsappMessage}`;

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const ctx = gsap.context(() => {
      // Não anima CTA com opacity 0 — precisa estar visível na primeira dobra
      gsap.from('.hero-line', {
        y: 16,
        opacity: 0,
        duration: 0.4,
        stagger: 0.06,
        ease: 'power3.out',
        delay: 0.02,
      });

      gsap.from('.hero-cta', {
        y: 10,
        duration: 0.35,
        ease: 'power3.out',
        delay: 0.08,
        clearProps: 'transform',
      });

      gsap.to('.hero-glow', {
        scale: 1.15,
        opacity: 0.55,
        duration: 4,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      id="home"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-dark-bg transition-colors duration-500"
    >
      <div className="hero-gradient absolute inset-0 z-0 opacity-70" />
      <div className="hero-glow absolute top-1/4 left-1/2 -translate-x-1/2 w-[min(600px,90vw)] h-[min(600px,90vw)] bg-burnt/25 rounded-full blur-[120px] z-[1]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 md:py-32 text-center w-full">
        <motion.p
          className="hero-line text-burnt font-heading font-semibold uppercase tracking-[0.25em] text-xs sm:text-sm mb-3 sm:mb-6"
          initial={false}
        >
          MS Digitais · Desenvolvimento Web
        </motion.p>

        <h1 className="mb-3 sm:mb-6">
          <span className="hero-line block text-[2rem] leading-[1.05] sm:text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-white">
            Sites que
          </span>
          <span className="hero-line block text-[2rem] leading-[1.05] sm:text-5xl md:text-7xl lg:text-8xl font-heading font-bold">
            <span className="text-burnt">impressionam</span>
          </span>
          <span className="hero-line block text-[2rem] leading-[1.05] sm:text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-white">
            e convertem
          </span>
        </h1>

        <p className="hero-line text-sm sm:text-lg md:text-xl text-cream/90 mb-4 sm:mb-8 font-body max-w-3xl mx-auto leading-relaxed">
          Sites institucionais para psicólogas, clínicas, advogados e profissionais autônomos —
          presença digital com credibilidade e resultado.
        </p>

        <p className="hero-cta text-xs sm:text-sm text-cream/80 font-body mb-3 sm:mb-4">
          Resposta em até 24h · Orçamento sem compromisso
        </p>

        <div className="hero-cta flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center w-full max-w-md sm:max-w-none mx-auto">
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-premium group bg-burnt hover:bg-primary-700 text-white px-6 sm:px-10 py-3.5 sm:py-4 min-h-[48px] rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-premium flex items-center justify-center gap-3 w-full sm:w-auto"
            data-cursor="pointer"
          >
            <FaWhatsapp className="text-xl shrink-0" />
            Solicitar Orçamento
          </a>
          <Link
            to="/orcamento"
            className="group px-6 sm:px-8 py-3 sm:py-3.5 min-h-[44px] rounded-xl font-semibold text-sm sm:text-base text-white border border-white/30 hover:border-burnt hover:bg-burnt/10 transition-all duration-300 flex items-center justify-center gap-2 w-full sm:w-auto"
            data-cursor="pointer"
          >
            <FaFileAlt />
            Preencher formulário
          </Link>
          <a
            href="#projetos"
            className="group px-6 sm:px-8 py-3 sm:py-3.5 min-h-[44px] rounded-xl font-semibold text-sm sm:text-base text-white/90 border border-white/20 hover:border-white/40 hover:bg-white/5 transition-all duration-300 flex items-center justify-center gap-2 w-full sm:w-auto"
            data-cursor="pointer"
          >
            Ver Portfólio
          </a>
        </div>

        <motion.div
          className="mt-8 sm:mt-16 md:mt-24 hidden sm:block"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <FaArrowDown className="w-5 h-5 mx-auto text-cream/50" aria-hidden="true" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
